import React from 'react'
import { always, cond, equals, identity, isNil, prop, propOr } from 'ramda'
import { formatPhoneNumber, formatMemberPortion } from 'sidecar-utilities'

import enhance from './enhance'
import {
  Wrapper,
  Address,
  Name,
  ImageColumn,
  InfoColumn,
  ExtraColumn,
  ImageWrapper,
  IndexLabel,
  NextIcon,
  PriceFairnessSpeciality,
  PriceFairnessTag,
  Specialty,
  Cost,
} from './styles'
import { environment, getPrimaryTaxonomySpecialty } from './utils'
import ListItem from '../../ListItem'
import Rating from '../../Rating'

const ListDoctorItem = ({
  lastName,
  firstName,
  organizationName,
  type,
  priceFairness,
  address,
  icon,
  index,
  npi,
  taxonomies,
  costAfterSidecar,
  ratingContent,
  // cms quality score props, hidden by FF, these props should
  //  be removed with the cms-quality-scores feature flag
  CMSTooltipsBool = true,
  rating,
  reviewCount,
  qualityCategoryScore,
  ...otherProps
}) => {
  const image = `https://${environment}doctor-image.sidecarhealth.com/care-providers/${npi}.png`
  const specialty = getPrimaryTaxonomySpecialty(taxonomies)

  return (
    <ListItem highlight {...otherProps} className="w-full">
      <Wrapper>
        <ImageColumn>
          <ImageWrapper isDefaultIcon={isNil(image)}>
            {index && <IndexLabel>{index}</IndexLabel>}

            <img
              alt="doctor"
              src={image}
              onError={e => {
                const fallbackUrl =
                  type === 'Organization'
                    ? 'https://assets.sidecarhealth.com/images/generic-facility.svg'
                    : 'https://assets.sidecarhealth.com/images/generic-doctor.svg'
                if (!e.target.src.includes(fallbackUrl)) {
                  e.target.src = fallbackUrl
                }
              }}
            />
          </ImageWrapper>
        </ImageColumn>

        <InfoColumn>
          <Name data-testid={`info_providerName_${npi}`}>
            {type === 'Individual'
              ? `${firstName} ${lastName}`
              : organizationName}
          </Name>
          {(priceFairness || specialty) && (
            <PriceFairnessSpeciality>
              {priceFairness && (
                <PriceFairnessTag
                  priceFairness={priceFairness}
                  data-testid={`info_priceFairness_${npi}`}
                />
              )}
              {specialty && (
                <Specialty data-testid={`info_providerSpecialty_${npi}`}>
                  {specialty}
                </Specialty>
              )}
            </PriceFairnessSpeciality>
          )}

          {address && (
            <Address data-testid={`info_providerAddress_${npi}`}>
              {propOr('', 'street', address)}
              {prop('street2', address) &&
                ', ' + propOr('', 'street2', address)}
              <br />
              {propOr('', 'city', address)}, {propOr('', 'state', address)}{' '}
              {propOr('', 'zip', address)}
              <br />
              {formatPhoneNumber(propOr('', 'phone', address))}
            </Address>
          )}
          {/* Double check the component is valid before rendering */}
          {ratingContent && ratingContent}
          <Rating
            reviewCount={reviewCount}
            rating={rating}
            qualityCategoryScore={qualityCategoryScore}
            CMSTooltipsBool={CMSTooltipsBool}
            data-testid={`info_providerRating_${npi}`}
          />
        </InfoColumn>

        {/* It's unlikely to have the icon AND cost simultaneously
            so I did not check that situation. */}
        <ExtraColumn>
          {!!icon &&
            cond([
              [React.isValidElement, identity],
              [isNil, always(<NextIcon />)],
              [equals(false), always(null)],
            ])(icon)}
          {!isNil(costAfterSidecar) && (
            <Cost data-testid={`info_providerCost_${npi}`}>
              {formatMemberPortion({ memberPortion: costAfterSidecar })}
            </Cost>
          )}
        </ExtraColumn>
      </Wrapper>
    </ListItem>
  )
}

export default enhance(ListDoctorItem)
